import { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SkeletonTable } from '../components/ui/page-skeleton';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Users, CurrencyDollar, Money, Receipt, FileText,
  ChartLineUp, Spinner, Download, Star, Crown, Heart, Moon,
  GenderMale, GenderFemale, Wallet, ArrowCircleUp, IdentificationCard,
  UserPlus, Clock, ChartPie, Calendar, Hash, CheckCircle, DownloadSimple,
  FolderOpen,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line, Legend,
} from 'recharts';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* Entity scope context — super_admin can pick an entity; other roles are
 * implicitly scoped server-side, so the context value just stays empty for
 * them. Children read `useEntityScope()` and append `?entity_id=…` to their
 * API calls. The value `'all'` (or empty) means "all entities". */
const EntityScopeCtx = createContext({ entityId: '' });
const useEntityScope = () => useContext(EntityScopeCtx);
const _scopeQS = (entityId) => (entityId && entityId !== 'all' ? `entity_id=${encodeURIComponent(entityId)}` : '');
const _withScope = (baseQs, entityId) => {
  const ent = _scopeQS(entityId);
  if (!ent) return baseQs || '';
  return baseQs ? `${baseQs}&${ent}` : ent;
};

const RANGES = [
  { value: 'today',          label: 'Today' },
  { value: 'this_month',     label: 'This Month' },
  { value: 'this_year',      label: 'This Year' },
  { value: 'specific_date',  label: 'Specific Date' },
  { value: 'specific_month', label: 'Specific Month' },
  { value: 'specific_year',  label: 'Specific Year' },
];

function resolveRange(range, date, month, year) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const yr = now.getFullYear();
  const mo = pad(now.getMonth() + 1);
  if (range === 'today') {
    const d = `${yr}-${mo}-${pad(now.getDate())}`;
    return { date_from: d, date_to: d };
  }
  if (range === 'this_month') {
    return { date_from: `${yr}-${mo}-01`, date_to: `${yr}-${mo}-31` };
  }
  if (range === 'this_year') {
    return { date_from: `${yr}-01-01`, date_to: `${yr}-12-31` };
  }
  if (range === 'specific_date' && date) {
    return { date_from: date, date_to: date };
  }
  if (range === 'specific_month' && month) {
    return { date_from: `${month}-01`, date_to: `${month}-31` };
  }
  if (range === 'specific_year' && year) {
    return { date_from: `${year}-01-01`, date_to: `${year}-12-31` };
  }
  return { date_from: null, date_to: null };
}

export default function AnalyticsPage() {
  const { isSuperAdmin } = useAuth();
  const [range, setRange] = useState('this_month');
  const [date, setDate] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [tab, setTab] = useState('subscribers');
  const [entityId, setEntityId] = useState('all');
  const [entities, setEntities] = useState([]);

  // Super admins load the entity list once for the scope selector.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/entities`, { headers: authHeaders() })
      .then((res) => { if (!cancelled) setEntities(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { /* selector just stays at "All Entities" */ });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  const params = useMemo(
    () => resolveRange(range, date, month, year),
    [range, date, month, year]
  );

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (params.date_from) p.set('date_from', params.date_from);
    if (params.date_to)   p.set('date_to',   params.date_to);
    if (entityId && entityId !== 'all') p.set('entity_id', entityId);
    return p.toString();
  }, [params, entityId]);

  return (
    <EntityScopeCtx.Provider value={{ entityId }}>
    <div className="space-y-5 pb-6" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Analytics
          </h1>
          <p className="text-sm text-[#78716C] mt-1">Operational and financial insights across your business.</p>
        </div>
        {isSuperAdmin && (
          <div className="space-y-1" data-testid="analytics-entity-scope">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Entity Scope
            </Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger
                className="min-w-[260px] border-[#EDE6D8] bg-white"
                data-testid="entity-scope-select"
              >
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entities.map((e) => (
                  <SelectItem key={e._id || e.id || e.middleware_id} value={String(e._id || e.id || e.middleware_id)}>
                    {e.entity_name || e.name || 'Unnamed entity'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        {/* Editorial section rail — each tab is a paper-toned card with
            icon, title and a 1-line descriptor. Active card flips to dark
            with an accent rail on the left edge. */}
        <TabsList
          className="bg-transparent p-0 h-auto grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2"
          data-testid="analytics-tabs"
        >
          {[
            { v: 'subscribers', label: 'Subscribers', icon: Users },
            { v: 'payments',    label: 'Recharges',   icon: CurrencyDollar },
            { v: 'royalties',   label: 'Royalties',   icon: Money },
            { v: 'utilities',   label: 'Utilities',   icon: Receipt },
            { v: 'reports',     label: 'Reports',     icon: FileText },
          ].map(({ v, label, icon: Icon }) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="group relative h-auto px-0 py-0 rounded-xl border border-[#EDE6D8] bg-white
                         data-[state=active]:bg-[#0A2540] data-[state=active]:border-[#0A2540]
                         data-[state=active]:shadow-md
                         transition-all duration-200 hover:border-[#0A2540]/30
                         overflow-hidden text-left flex flex-col items-start"
            >
              {/* Active rail */}
              <span
                className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C9A35B] opacity-0
                           group-data-[state=active]:opacity-100 transition-opacity"
                aria-hidden
              />
              <div className="w-full flex items-center gap-2 px-3 py-2">
                <div
                  className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center
                             bg-[#FCFAF6] text-[#0A2540]
                             group-data-[state=active]:bg-white/10
                             group-data-[state=active]:text-[#C9A35B]
                             transition-colors"
                >
                  <Icon size={13} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-[12px] font-semibold leading-tight
                               text-[#1C1917] group-data-[state=active]:text-white"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {label}
                  </p>
                </div>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Frame separating the active tab's analytics from the tab strip above */}
        <div
          className="mt-4 rounded-2xl border border-[#EDE6D8] bg-[#FFFFFF] p-5 md:p-6
                     shadow-[0_1px_3px_rgba(15,23,42,0.04)] relative"
          data-testid="analytics-frame"
        >
          {/* Subtle gold accent rail to echo the active-tab styling */}
          <span
            className="absolute left-0 top-0 bottom-0 w-1 bg-[#C9A35B]/30 rounded-l-2xl"
            aria-hidden
          />

          {/* Contextual range filter — sits just below the tab strip so the
              date scope applies to whichever tab is active. Hidden on tabs
              that don't take a global range (Subscribers, Recharges, and the
              coming-soon tabs). */}
          {!['payments', 'subscribers', 'royalties', 'utilities', 'reports'].includes(tab) && (
          <div
            className="flex items-end justify-between gap-3 flex-wrap mb-5 pb-4 border-b border-dashed border-[#EDE6D8]"
            data-testid="analytics-filter"
          >
            <div className="flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Date Range
              </p>
              <p className="text-xs text-[#1C1917] mt-0.5">
                {params.date_from || params.date_to
                  ? <>Showing data from <span className="font-mono font-semibold">{params.date_from || '∞'}</span> to <span className="font-mono font-semibold">{params.date_to || 'today'}</span></>
                  : <>Showing all available data</>}
              </p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Range</Label>
                <Select value={range} onValueChange={setRange}>
                  <SelectTrigger className="w-44 border-[#EDE6D8]" data-testid="range-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {range === 'specific_date' && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Date</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 border-[#EDE6D8]" data-testid="range-date" />
                </div>
              )}
              {range === 'specific_month' && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Month</Label>
                  <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 border-[#EDE6D8]" data-testid="range-month" />
                </div>
              )}
              {range === 'specific_year' && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Year</Label>
                  <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-32 border-[#EDE6D8]" data-testid="range-year" />
                </div>
              )}
            </div>
          </div>
          )}

          <TabsContent value="subscribers" className="mt-0 focus-visible:outline-none"><SubscribersTab /></TabsContent>
          <TabsContent value="payments"    className="mt-0 focus-visible:outline-none"><PaymentsTab /></TabsContent>
          <TabsContent value="royalties"   className="mt-0 focus-visible:outline-none"><RoyaltiesTab /></TabsContent>
          <TabsContent value="utilities"   className="mt-0 focus-visible:outline-none"><UtilitiesTab /></TabsContent>
          <TabsContent value="reports"     className="mt-0 focus-visible:outline-none"><ReportsTab /></TabsContent>
        </div>
      </Tabs>
    </div>
    </EntityScopeCtx.Provider>
  );
}

function useEndpoint(path, qs) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}${path}${qs ? `?${qs}` : ''}`, { headers: authHeaders(), withCredentials: true })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) toast.error(formatApiError(err, 'Failed to load')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, qs]);
  return { loading, data };
}

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString();

function KpiCard({ label, value, accent = 'text-[#1C1917]', sub }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className={`mt-2 text-2xl font-bold font-mono ${accent}`}>{value}</p>
      {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

function BreakdownBars({ title, data, color = '#0A2540', valueFmt = fmtInt }) {
  const max = Math.max(...(data || []).map(d => d.value), 0) || 1;
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      {(!data || data.length === 0) ? <p className="text-xs text-[#78716C] py-6 text-center">No data.</p> : (
        <div className="space-y-2.5">
          {data.map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs text-[#1C1917] w-44 truncate">{d.name}</span>
              <div className="flex-1 h-2 rounded-full bg-[#FCFAF6] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(d.value / max) * 100}%`, backgroundColor: color }} />
              </div>
              <span className="text-xs font-mono text-[#0A2540] w-20 text-right">{valueFmt(d.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Wrap({ loading, children }) {
  if (loading) return <div className="py-2"><SkeletonTable rows={6} cols={4} testid="analytics-loading" /></div>;
  return children;
}

function PerfCard({ label, value, total, color, icon: Icon, hidePercent = false }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        {Icon && (
          <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
            <Icon size={14} weight="fill" />
          </span>
        )}
      </div>
      <p className="mt-1.5 text-2xl font-bold font-mono" style={{ color }}>{fmtInt(value)}</p>
      {!hidePercent && total > 0 && (
        <>
          <p className="text-[11px] text-[#78716C] font-mono">{pct.toFixed(1)}% of total</p>
          <div className="mt-2 h-1.5 rounded-full bg-[#FCFAF6] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
          </div>
        </>
      )}
    </div>
  );
}

function PercentBars({ title, data, palette = ['#0A2540', '#C9A35B', '#059669', '#7C3AED', '#DC2626', '#0EA5E9'], bare = false }) {
  const total = (data || []).reduce((acc, d) => acc + (d.value || 0), 0) || 1;
  const body = (
    <>
      {(!data || data.length === 0) ? <p className="text-xs text-[#78716C] py-6 text-center">No data.</p> : (
        <div className="space-y-3">
          {data.map((d, i) => {
            const pct = (d.value / total) * 100;
            const color = palette[i % palette.length];
            return (
              <div key={d.name} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#1C1917] font-medium truncate pr-2">{d.name}</span>
                  <span className="font-mono text-[#78716C] shrink-0">
                    {fmtInt(d.value)} · <span style={{ color }}>{pct.toFixed(1)}%</span>
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-[#FCFAF6] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
  if (bare) return body;
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 h-full">
      <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      {body}
    </div>
  );
}

function GenderDonut({ data, bare = false }) {
  const male = data?.male || 0;
  const female = data?.female || 0;
  const other = data?.other || 0;
  const total = male + female + other;
  // Build SVG donut.
  const R = 56;
  const C = 2 * Math.PI * R;
  const malePct = total > 0 ? male / total : 0;
  const femalePct = total > 0 ? female / total : 0;
  const otherPct = total > 0 ? other / total : 0;
  // Stroke-dasharray segments (length, gap)
  const seg = (p) => `${p * C} ${C}`;
  const offsetM = 0;
  const offsetF = -malePct * C;
  const offsetO = -(malePct + femalePct) * C;
  const body = total === 0
    ? <p className="text-xs text-[#78716C] py-6 text-center">No data.</p>
    : (
      <div className="flex items-center gap-6">
        <svg viewBox="0 0 140 140" className="w-32 h-32 shrink-0">
          <circle cx="70" cy="70" r={R} fill="none" stroke="#FCFAF6" strokeWidth="18" />
          {male > 0 && (
            <circle
              cx="70" cy="70" r={R} fill="none" stroke="#0A2540" strokeWidth="18"
              strokeDasharray={seg(malePct)} strokeDashoffset={offsetM}
              transform="rotate(-90 70 70)" strokeLinecap="butt"
            />
          )}
          {female > 0 && (
            <circle
              cx="70" cy="70" r={R} fill="none" stroke="#C9A35B" strokeWidth="18"
              strokeDasharray={seg(femalePct)} strokeDashoffset={offsetF}
              transform="rotate(-90 70 70)" strokeLinecap="butt"
            />
          )}
          {other > 0 && (
            <circle
              cx="70" cy="70" r={R} fill="none" stroke="#94A3B8" strokeWidth="18"
              strokeDasharray={seg(otherPct)} strokeDashoffset={offsetO}
              transform="rotate(-90 70 70)" strokeLinecap="butt"
            />
          )}
          <text x="70" y="68" textAnchor="middle" className="font-mono" style={{ fontSize: '20px', fontWeight: 700, fill: '#0A2540' }}>{fmtInt(total)}</text>
          <text x="70" y="86" textAnchor="middle" style={{ fontSize: '9px', fill: '#78716C', letterSpacing: '0.1em' }}>TOTAL</text>
        </svg>
        <div className="flex-1 space-y-2">
          <DonutLegend color="#0A2540" icon={GenderMale} label="Male"   value={male}   total={total} />
          <DonutLegend color="#C9A35B" icon={GenderFemale} label="Female" value={female} total={total} />
          {other > 0 && (
            <DonutLegend color="#94A3B8" icon={Users} label="Other" value={other} total={total} />
          )}
        </div>
      </div>
    );
  if (bare) return body;
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 h-full">
      <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Gender Distribution</p>
      {body}
    </div>
  );
}

function DonutLegend({ color, icon: Icon, label, value, total }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
        <Icon size={14} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#1C1917]">{label}</p>
        <p className="text-[11px] font-mono text-[#78716C]">{fmtInt(value)} · {pct.toFixed(1)}%</p>
      </div>
    </div>
  );
}

function LeaderRow({ rank, name, sub, value, accent = '#0A2540', icon: Icon, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="grid grid-cols-[28px_1fr_auto] items-center gap-3 py-2.5 border-b border-[#EDE6D8] last:border-b-0">
      <span className="text-[10px] font-mono text-[#78716C] font-semibold">#{rank}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#1C1917] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {name}
        </p>
        <p className="text-[11px] text-[#78716C] font-mono flex items-center gap-1.5 mt-0.5">
          {Icon && <Icon size={11} weight="duotone" />}
          {sub || '—'}
        </p>
        <div className="mt-1.5 h-1.5 rounded-full bg-[#FCFAF6] overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} />
        </div>
      </div>
      <span className="text-sm font-mono font-semibold shrink-0" style={{ color: accent }}>
        {fmtInt(value)}
      </span>
    </div>
  );
}

function LeaderboardCard({ title, icon: Icon, rows, accent = '#0A2540', valueKey, subRender, bare = false }) {
  const max = Math.max(...(rows || []).map(r => r[valueKey] || 0), 0) || 1;
  const body = (!rows || rows.length === 0) ? (
    <p className="text-xs text-[#78716C] py-6 text-center">No data.</p>
  ) : (
    <div>
      {rows.map((r, i) => (
        <LeaderRow
          key={r.sub_id || i}
          rank={i + 1}
          name={r.name}
          sub={subRender ? subRender(r) : r.card_no}
          value={r[valueKey]}
          accent={accent}
          icon={IdentificationCard}
          max={max}
        />
      ))}
    </div>
  );
  if (bare) return body;
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 h-full">
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
            <Icon size={14} weight="fill" />
          </span>
        )}
        <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      </div>
      {body}
    </div>
  );
}

function AcquisitionTrend({ data }) {
  const rows = data || [];
  const total = rows.reduce((acc, r) => acc + (r.count || 0), 0);
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5 h-full">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
          <UserPlus size={14} weight="fill" />
        </span>
        <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          Acquisition Trend · Past 6 Months
        </p>
      </div>
      {total === 0 ? (
        <p className="text-xs text-[#78716C] py-10 text-center">No registrations in the last 6 months.</p>
      ) : (
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
                stroke="#78716C"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
                stroke="#78716C"
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
              />
              <ReTooltip
                cursor={{ fill: '#0A254010' }}
                contentStyle={{ fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '8px', fontSize: 12 }}
                formatter={(v) => [`${v} subscriber${v === 1 ? '' : 's'}`, 'New registrations']}
                labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {rows.map((r, i) => (
                  <Cell key={i} fill={i === rows.length - 1 ? '#0A2540' : '#C9A35B'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function SubscribersTab() {
  // Subscribers analytics show a stable view of the live subscriber base —
  // no date-range scoping (the data set is the entire current population, so
  // per-section chips don't add insight).
  const { entityId } = useEntityScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = _scopeQS(entityId);
    axios.get(`${API_URL}/api/analytics/subscribers${qs ? `?${qs}` : ''}`, { headers: authHeaders(), withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) toast.error(formatApiError(err, 'Failed to load')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityId]);

  if (loading || !data) return <Wrap loading={loading} />;

  return (
    <Wrap loading={loading}>
      <div className="space-y-5">
        {/* 1 · Performance stats (Total + 4 buckets) — all navy digits */}
        <PanelCard title="Performance" icon={Users}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="subs-stat-cards">
            <PerfCard label="Total Subscribers" value={data.total_subscribers || 0} total={data.total_subscribers || 0} color="#0A2540" icon={Users} hidePercent />
            <PerfCard label="Excellent"         value={data.by_performance?.excellent || 0} total={data.total_subscribers || 0} color="#0A2540" icon={Crown} />
            <PerfCard label="Good"              value={data.by_performance?.good      || 0} total={data.total_subscribers || 0} color="#0A2540" icon={Heart} />
            <PerfCard label="Normal"            value={data.by_performance?.normal    || 0} total={data.total_subscribers || 0} color="#0A2540" icon={Star}  />
            <PerfCard label="Dormant"           value={data.by_performance?.dormant   || 0} total={data.total_subscribers || 0} color="#0A2540" icon={Moon}  />
          </div>
        </PanelCard>

        {/* 2 · By Category + Gender Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard title="By Category" icon={IdentificationCard}>
            <PercentBars data={data.by_category} palette={['#0A2540']} bare />
          </PanelCard>

          <PanelCard title="Gender Distribution" icon={GenderMale}>
            <GenderDonut data={data.by_gender} bare />
          </PanelCard>
        </div>

        {/* 3 · Top Campuses + Top 5 Wallet Balance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard title="Top Campuses" icon={Users}>
            <PercentBars data={data.by_campus} palette={['#0A2540']} bare />
          </PanelCard>

          <PanelCard title="Top 5 · Wallet Balance" icon={Wallet}>
            <LeaderboardCard
              rows={data.top_by_wallet}
              valueKey="wallet_bal"
              subRender={(r) => r.card_no ? `Card ${r.card_no}` : 'No card'}
              accent="#0A2540"
              bare
            />
          </PanelCard>
        </div>

        {/* 4 · Top 5 Recharges + Acquisition Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard title="Top 5 · Recharge Value" icon={ArrowCircleUp}>
            <LeaderboardCard
              rows={data.top_by_recharges}
              valueKey="total_recharges"
              subRender={(r) => r.card_no ? `Card ${r.card_no}` : 'No card'}
              accent="#0A2540"
              bare
            />
          </PanelCard>

          <AcquisitionTrend data={data.acquisitions_by_month} />
        </div>
      </div>
    </Wrap>
  );
}

/* ============ RECHARGES (payments) ANALYTICS ============
   Positive wallet movements only. Each section has its own scope chip
   (Today / Month / Year). Trends are fixed at 7d + 6m.
   ======================================================= */
const CHANNEL_COLORS = ['#0A2540', '#C9A35B', '#059669', '#2563EB', '#F59E0B', '#7C3AED', '#EC4899', '#10B981'];

const SECTION_SCOPES = [
  { value: 'today',      label: 'Today' },
  { value: 'this_month', label: 'Month' },
  { value: 'this_year',  label: 'Year'  },
];

function resolveSectionScope(scope) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const m = pad(now.getMonth() + 1);
  const d = pad(now.getDate());
  if (scope === 'today')      return { date_from: `${y}-${m}-${d}`, date_to: `${y}-${m}-${d}` };
  if (scope === 'this_year')  return { date_from: `${y}-01-01`,     date_to: `${y}-12-31`     };
  /* this_month default */     return { date_from: `${y}-${m}-01`,   date_to: `${y}-${m}-31`   };
}

function fmtH(h) {
  if (h === null || h === undefined) return '—';
  const n = Number(h);
  if (!Number.isFinite(n)) return '—';
  const ampm = n >= 12 ? 'PM' : 'AM';
  const hr12 = ((n + 11) % 12) + 1;
  return `${String(hr12).padStart(2, '0')}:00 ${ampm}`;
}

function PaymentsTab() {
  // Per-section scope chips. Trends ignore these.
  const { entityId } = useEntityScope();
  const [totalsScope,       setTotalsScope]       = useState('this_month');
  const [channelAmtScope,   setChannelAmtScope]   = useState('this_month');
  const [channelCountScope, setChannelCountScope] = useState('this_month');
  const [peaksScope,        setPeaksScope]        = useState('this_month');

  // Fetch all three scopes once in parallel and cache; each card reads its slice.
  const [dataByScope, setDataByScope] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const scopes = ['today', 'this_month', 'this_year'];
    Promise.all(scopes.map(async (s) => {
      const r = resolveSectionScope(s);
      const qs = _withScope(`date_from=${r.date_from}&date_to=${r.date_to}`, entityId);
      const res = await axios.get(`${API_URL}/api/analytics/payments?${qs}`,
        { headers: authHeaders(), withCredentials: true });
      return [s, res.data];
    }))
      .then((pairs) => { if (!cancelled) setDataByScope(Object.fromEntries(pairs)); })
      .catch((err) => { if (!cancelled) toast.error(formatApiError(err, 'Failed to load')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityId]);

  if (loading || !Object.keys(dataByScope).length) return <Wrap loading={loading} />;

  const totalsData       = dataByScope[totalsScope]       || dataByScope.this_month;
  const channelAmtData   = dataByScope[channelAmtScope]   || dataByScope.this_month;
  const channelCountData = dataByScope[channelCountScope] || dataByScope.this_month;
  const peaksData        = dataByScope[peaksScope]        || dataByScope.this_month;
  const trendsData       = dataByScope.this_year          || dataByScope.this_month; // trend windows are server-fixed

  const channelsByAmt   = channelAmtData.by_channel   || [];
  const channelsByCount = channelCountData.by_channel || [];
  const totalAmtForPct   = channelsByAmt.reduce((a, c) => a + Number(c.amount || 0), 0) || 1;
  const totalCountForPct = channelsByCount.reduce((a, c) => a + Number(c.count  || 0), 0) || 1;

  // Server-computed average amount per transaction in the totals scope
  const avgPerTxn = Number(totalsData.averages?.amount_per_txn || 0);

  return (
    <Wrap loading={loading}>
      <div className="space-y-5">
        {/* 1 · Totals (3 KPIs incl. avg / txn) */}
        <PanelCard
          title="Totals"
          subtitle="Total recharges in scope"
          icon={CurrencyDollar}
          scope={totalsScope}
          onScopeChange={setTotalsScope}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiCard label="Total Recharges · Amount" value={fmt(totalsData.total_amount)} accent="text-[#0A2540]" />
            <KpiCard label="Total Recharges · Count"  value={fmtInt(totalsData.total_count)} accent="text-[#0A2540]" />
            <KpiCard label="Avg Amount / Transaction" value={fmt(avgPerTxn)} accent="text-[#0A2540]" />
          </div>
        </PanelCard>

        {/* 2 · Trends — second from the top (fixed windows) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <TrendCard title="Past 7 Days"   data={trendsData.trend_7d || []} xKey="label" />
          <TrendCard title="Past 6 Months" data={trendsData.trend_6m || []} xKey="label" />
        </div>

        {/* 3 · Channel Distribution — duo (by Amount + by Count) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard
            title="Channel · By Amount"
            subtitle="Share of recharge value"
            icon={ChartPie}
            scope={channelAmtScope}
            onScopeChange={setChannelAmtScope}
          >
            <ChannelDonut
              channels={channelsByAmt}
              dataKey="amount"
              denom={totalAmtForPct}
              valueFmt={fmt}
              secondaryKey="count"
              secondaryLabel="txns"
            />
          </PanelCard>

          <PanelCard
            title="Channel · By Transactions"
            subtitle="Share of transaction count"
            icon={ChartPie}
            scope={channelCountScope}
            onScopeChange={setChannelCountScope}
          >
            <ChannelDonut
              channels={channelsByCount}
              dataKey="count"
              denom={totalCountForPct}
              valueFmt={fmtInt}
              secondaryKey="amount"
              secondaryLabel="amt"
            />
          </PanelCard>
        </div>

        {/* 4 · Peaks */}
        <PanelCard
          title="Peaks"
          subtitle="Highest activity in a single hour-of-day"
          icon={Clock}
          scope={peaksScope}
          onScopeChange={setPeaksScope}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PeakTile
              label="Peak Hour · By Amount"
              hour={peaksData.peaks?.peak_hour_value?.hour}
              metric={fmt(peaksData.peaks?.peak_hour_value?.amount)}
              metricLabel="amount"
            />
            <PeakTile
              label="Peak Hour · By Transactions"
              hour={peaksData.peaks?.peak_hour_count?.hour}
              metric={fmtInt(peaksData.peaks?.peak_hour_count?.count)}
              metricLabel="transactions"
            />
          </div>
        </PanelCard>
      </div>
    </Wrap>
  );
}

function ChannelDonut({ channels, dataKey, denom, valueFmt, secondaryKey, secondaryLabel }) {
  if (!channels || channels.length === 0) {
    return <EmptyState>No recharges in scope.</EmptyState>;
  }
  // Hide rows where the primary metric is zero (e.g. by-count donut with all-amount-but-no-count would be odd)
  const rows = channels.filter((c) => Number(c[dataKey] || 0) > 0);
  if (rows.length === 0) {
    return <EmptyState>No recharges in scope.</EmptyState>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-4 items-center">
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey={dataKey}
              nameKey="name"
              innerRadius={48}
              outerRadius={78}
              paddingAngle={2}
              stroke="#FCFAF6"
              strokeWidth={2}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
              ))}
            </Pie>
            <ReTooltip
              formatter={(v) => valueFmt(v)}
              contentStyle={{ background: '#0A2540', border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
              itemStyle={{ color: '#FCFAF6' }}
              labelStyle={{ color: '#C9A35B', fontWeight: 600 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="space-y-1.5">
        {rows.map((c, i) => {
          const pct = (Number(c[dataKey] || 0) / denom) * 100;
          const secondary = c[secondaryKey];
          return (
            <li key={c.name} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
              <span className="font-semibold text-[#1C1917] flex-1 truncate">{c.name}</span>
              <span className="font-mono tabular-nums text-[#0A2540]">{valueFmt(c[dataKey])}</span>
              <span className="font-mono tabular-nums text-[#78716C] w-12 text-right">{pct.toFixed(1)}%</span>
              <span className="font-mono tabular-nums text-[#78716C] w-20 text-right truncate">
                {secondaryLabel} {secondaryKey === 'amount' ? fmt(secondary) : fmtInt(secondary)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ScopeChip({ value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-[#EDE6D8] p-0.5 bg-[#FCFAF6]" data-testid="section-scope">
      {SECTION_SCOPES.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm transition-colors ${
            value === o.value
              ? 'bg-[#0A2540] text-white shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid={`scope-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PanelCard({ title, subtitle, icon: Icon, children, scope, onScopeChange }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={14} weight="duotone" className="text-[#0A2540] shrink-0" />}
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1C1917] truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {title}
          </p>
          {subtitle && <p className="text-[11px] text-[#78716C] truncate">· {subtitle}</p>}
        </div>
        {scope !== undefined && onScopeChange && (
          <ScopeChip value={scope} onChange={onScopeChange} />
        )}
      </div>
      {children}
    </div>
  );
}

function PeakTile({ label, hour, metric, metricLabel }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </p>
      <div className="mt-2 flex items-end gap-3">
        <p className="text-2xl font-bold font-mono text-[#0A2540]">{fmtH(hour)}</p>
        <p className="text-xs text-[#78716C] pb-1">
          <span className="font-mono font-semibold text-[#1C1917]">{metric}</span> {metricLabel}
        </p>
      </div>
    </div>
  );
}

function TrendCard({ title, data, xKey }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <ChartLineUp size={14} weight="duotone" className="text-[#0A2540]" />
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {title}
        </p>
        <span className="text-[11px] text-[#78716C]">· Amount &amp; count</span>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#EDE6D8" strokeDasharray="3 3" />
            <XAxis dataKey={xKey} stroke="#78716C" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} />
            <YAxis yAxisId="left"  stroke="#0A2540" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} width={56}
                   tickFormatter={(v) => Number(v).toLocaleString(undefined, { notation: 'compact' })} />
            <YAxis yAxisId="right" orientation="right" stroke="#C9A35B" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} width={28}
                   tickFormatter={(v) => Number(v).toLocaleString(undefined, { notation: 'compact' })} />
            <ReTooltip
              contentStyle={{ background: '#0A2540', border: 'none', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
              itemStyle={{ color: '#FCFAF6' }}
              labelStyle={{ color: '#C9A35B', fontWeight: 600 }}
              formatter={(v, name) => name === 'amount' ? fmt(v) : fmtInt(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="left"  type="monotone" dataKey="amount" name="amount" stroke="#0A2540" strokeWidth={2.2} dot={{ r: 3, fill: '#0A2540' }} />
            <Line yAxisId="right" type="monotone" dataKey="count"  name="count"  stroke="#C9A35B" strokeWidth={2}   dot={{ r: 3, fill: '#C9A35B' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <p className="text-center text-xs text-[#78716C] py-8" data-testid="analytics-empty">{children}</p>
  );
}

function ComingSoon({ icon: Icon, title, blurb }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#EDE6D8] bg-[#FCFAF6]/30 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="coming-soon">
      <div className="w-14 h-14 rounded-2xl bg-white border border-[#EDE6D8] flex items-center justify-center mb-4">
        <Icon size={26} weight="duotone" className="text-[#0A2540]" />
      </div>
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C9A35B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        Coming Soon
      </p>
      <h3 className="text-xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        {title}
      </h3>
      <p className="text-sm text-[#78716C] max-w-md">{blurb}</p>
    </div>
  );
}

function RoyaltiesTab() {
  const { entityId } = useEntityScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const qs = _scopeQS(entityId);
        const res = await axios.get(`${API_URL}/api/analytics/royalties${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e, 'Failed to load royalty analytics'));
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [entityId]);

  if (loading) return <SkeletonTable rows={5} />;
  if (!data) return <ComingSoon icon={Money} title="No royalty data yet" blurb="Royalty figures will populate automatically once refills start flowing through the network." />;

  const head     = data.headline || {};
  const status   = data.invoice_status || {};
  const top5     = data.top_campuses || [];
  const series   = data.series_6mo || [];
  const thisYear = data.this_year;
  const fmt = (n) => Number(n || 0).toLocaleString();

  return (
    <div className="space-y-6" data-testid="royalties-tab">
      {/* Headline strip — Year · Month · Paid · Pending */}
      <div
        className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden"
        data-testid="royalty-headline-strip"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#EDE6D8]">
          <HeadlineTile
            eyebrow="Royalty"
            subtitle={`Total · ${thisYear}`}
            value={fmt(head.total_year)}
            testid="hl-roy-year"
          />
          <HeadlineTile
            eyebrow="Royalty"
            subtitle="This Month"
            value={fmt(head.total_month)}
            testid="hl-roy-month"
          />
          <HeadlineTile
            eyebrow="Paid"
            subtitle="Royalty invoices"
            value={fmt(status.paid?.amount)}
            amount={`${Number(status.paid?.count || 0).toLocaleString()} invoice${(status.paid?.count || 0) === 1 ? '' : 's'}`}
            testid="hl-roy-paid"
          />
          <HeadlineTile
            eyebrow="Pending"
            subtitle="Royalty invoices"
            value={fmt(status.pending?.amount)}
            amount={`${Number(status.pending?.count || 0).toLocaleString()} invoice${(status.pending?.count || 0) === 1 ? '' : 's'}`}
            testid="hl-roy-pending"
          />
        </div>
      </div>

      {/* Trend + Top 5 campuses */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Royalty Trend" subtitle="Past 6 months">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDE6D8" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                <ReTooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #EDE6D8' }}
                  formatter={(v) => Number(v || 0).toLocaleString()}
                />
                <Bar dataKey="royalty" name="Royalty" fill="#0A2540" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div>
          <ChartCard title="Top 5 Campuses" subtitle={`By royalty earnings · ${thisYear}`}>
            {top5.length === 0 ? (
              <p className="text-sm text-[#78716C] py-6 text-center">No royalty data yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">#</th>
                    <th className="text-left px-3 py-2 font-semibold">Campus</th>
                    <th className="text-right px-3 py-2 font-semibold">Royalty</th>
                  </tr>
                </thead>
                <tbody>
                  {top5.map((c, i) => (
                    <tr key={c.id} className="border-t border-[#EDE6D8]" data-testid={`top-campuses-royalty-${i + 1}`}>
                      <td className="px-3 py-2 font-mono text-[#78716C]">{i + 1}</td>
                      <td className="px-3 py-2 text-[#1A1D1C]">{c.name}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-[#0A2540]">{Number(c.royalty_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <div className="mb-3">
        <p className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {title}
        </p>
        {subtitle && (
          <p className="text-sm font-semibold text-[#1A1D1C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function UtilitiesTab() {
  const { entityId } = useEntityScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const qs = _scopeQS(entityId);
        const res = await axios.get(`${API_URL}/api/analytics/utilities${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) toast.error(formatApiError(e, 'Failed to load utility analytics'));
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [entityId]);

  if (loading) return <SkeletonTable rows={5} />;
  if (!data) return <ComingSoon icon={Receipt} title="No utility data yet" blurb="Once meter readings are logged this dashboard will populate automatically." />;

  const {
    this_year: thisYear, prev_year: prevYear,
    cards, series_6mo, top_stores, top_campuses, cost_split_6mo,
  } = data;

  return (
    <div className="space-y-6" data-testid="utilities-tab">
      {/* Headline strip — Water · Electricity · Paid · Pending in one card */}
      <UtilityHeadlineStrip
        thisYear={thisYear}
        prevYear={prevYear}
        water={cards?.water}
        electricity={cards?.electricity}
        paid={data?.invoice_status?.paid}
        pending={data?.invoice_status?.pending}
      />

      {/* Row — 6-month comparison + cost split donut side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Water vs Electricity — Past 6 Months" subtitle="Amount billed by month">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={series_6mo} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDE6D8" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                <ReTooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #EDE6D8' }}
                  formatter={(v) => Number(v || 0).toLocaleString()}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} />
                <Bar dataKey="water_amount"       name="Water"       fill="#0A2540" radius={[4, 4, 0, 0]} />
                <Bar dataKey="electricity_amount" name="Electricity" fill="#C9A35B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div>
          <ChartCard title="Cost Split" subtitle="Electricity vs Water · past 6 months">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={cost_split_6mo}
                  cx="50%" cy="50%"
                  innerRadius={55} outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {cost_split_6mo?.map((entry, idx) => (
                    <Cell key={idx} fill={entry.name === 'Water' ? '#0A2540' : '#C9A35B'} />
                  ))}
                </Pie>
                <ReTooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #EDE6D8' }}
                  formatter={(v) => Number(v || 0).toLocaleString()}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 space-y-2">
              {cost_split_6mo?.map((c) => {
                const total = cost_split_6mo.reduce((acc, x) => acc + (x.value || 0), 0) || 1;
                const pct = ((c.value || 0) / total) * 100;
                const color = c.name === 'Water' ? '#0A2540' : '#C9A35B';
                return (
                  <div key={c.name} className="flex items-center justify-between text-sm" data-testid={`cost-split-${c.name.toLowerCase()}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                      <span className="text-[#1A1D1C]">{c.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-mono font-semibold text-[#0A2540]">{Number(c.value || 0).toLocaleString()}</span>
                      <span className="ml-2 text-[10px] text-[#78716C] font-mono">{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Row 4 — Top 5 Stores by usage (Water / Electricity) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopUsageTable
          title="Top 5 Stores · Water"
          rows={top_stores?.water}
          utility="water"
          unit="m³"
          testid="top-stores-water"
        />
        <TopUsageTable
          title="Top 5 Stores · Electricity"
          rows={top_stores?.electricity}
          utility="electricity"
          unit="kWh"
          testid="top-stores-electricity"
        />
      </div>

      {/* Row 5 — Top 5 Campuses by usage (Water / Electricity) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopUsageTable
          title="Top 5 Campuses · Water"
          rows={top_campuses?.water}
          utility="water"
          unit="m³"
          testid="top-campuses-water"
        />
        <TopUsageTable
          title="Top 5 Campuses · Electricity"
          rows={top_campuses?.electricity}
          utility="electricity"
          unit="kWh"
          testid="top-campuses-electricity"
        />
      </div>
    </div>
  );
}

/* ─── helpers used by the Utilities tab ─────────────────────────────────── */

function YoyDelta({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-[10px] text-[#78716C]">n/a</span>;
  const up = pct >= 0;
  return (
    <span className={`text-[10px] font-bold ${up ? 'text-[#059669]' : 'text-[#EF4444]'}`}>
      {up ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function UtilityHeadlineStrip({ thisYear, prevYear, water, electricity, paid, pending }) {
  const fmt = (n) => Number(n || 0).toLocaleString();
  // Delta % for the YoY arrow (returns null when prev is 0 — keeps the
  // tile clean and avoids divide-by-zero noise).
  const delta = (a, b) => (b ? ((a - b) / b) * 100 : null);
  const wAmtDelta = delta(water?.this_year?.amount,       water?.prev_year?.amount);
  const eAmtDelta = delta(electricity?.this_year?.amount, electricity?.prev_year?.amount);
  return (
    <div
      className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden"
      data-testid="utility-headline-strip"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#EDE6D8]">
        <HeadlineTile
          eyebrow="Water"
          subtitle={`Usage · ${thisYear}`}
          value={fmt(water?.this_year?.usage)}
          unit="m³"
          amount={fmt(water?.this_year?.amount)}
          footerLeft={`vs ${prevYear}: ${fmt(water?.prev_year?.amount)}`}
          deltaPct={wAmtDelta}
          testid="hl-water"
        />
        <HeadlineTile
          eyebrow="Electricity"
          subtitle={`Usage · ${thisYear}`}
          value={fmt(electricity?.this_year?.usage)}
          unit="kWh"
          amount={fmt(electricity?.this_year?.amount)}
          footerLeft={`vs ${prevYear}: ${fmt(electricity?.prev_year?.amount)}`}
          deltaPct={eAmtDelta}
          testid="hl-electricity"
        />
        <HeadlineTile
          eyebrow="Paid"
          subtitle="Invoices"
          value={fmt(paid?.amount)}
          amount={`${Number(paid?.count || 0).toLocaleString()} invoice${(paid?.count || 0) === 1 ? '' : 's'}`}
          testid="hl-paid"
        />
        <HeadlineTile
          eyebrow="Pending"
          subtitle="Invoices"
          value={fmt(pending?.amount)}
          amount={`${Number(pending?.count || 0).toLocaleString()} invoice${(pending?.count || 0) === 1 ? '' : 's'}`}
          testid="hl-pending"
        />
      </div>
    </div>
  );
}

function HeadlineTile({ eyebrow, subtitle, value, unit, amount, footerLeft, deltaPct, testid }) {
  return (
    <div className="p-5" data-testid={testid}>
      <p
        className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {eyebrow}
      </p>
      <p
        className="text-xs text-[#78716C] mt-0.5"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {subtitle}
      </p>
      <p
        className="mt-3 text-2xl font-bold text-[#0A2540] font-mono leading-none"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {value}
        {unit && (
          <span className="text-[11px] text-[#78716C] font-medium ml-1.5">{unit}</span>
        )}
      </p>
      {amount && (
        <p
          className="mt-1.5 text-sm text-[#0A2540] font-mono"
        >
          {amount}
        </p>
      )}
      {(footerLeft || deltaPct !== undefined) && (
        <div className="mt-3 pt-3 border-t border-[#EDE6D8] flex items-center justify-between text-[10px]">
          {footerLeft && (
            <span
              className="text-[#78716C] truncate"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {footerLeft}
            </span>
          )}
          {deltaPct !== undefined && <YoyDelta pct={deltaPct} />}
        </div>
      )}
    </div>
  );
}

function TopUsageTable({ title, rows, utility, unit, testid }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#EDE6D8]">
        <p className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {title}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            <tr>
              <th className="text-left px-4 py-2 font-semibold">#</th>
              <th className="text-left px-4 py-2 font-semibold">Name</th>
              <th className="text-right px-4 py-2 font-semibold">Usage ({unit})</th>
              <th className="text-right px-4 py-2 font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {!rows || rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[#78716C]">No data yet.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} className="border-t border-[#EDE6D8]" data-testid={`${testid}-row-${i + 1}`}>
                <td className="px-4 py-2 font-mono text-[#78716C]">{i + 1}</td>
                <td className="px-4 py-2 text-[#1A1D1C]">{r.name}</td>
                <td className="px-4 py-2 text-right font-mono text-[#1A1D1C]">{Number(r[`${utility}_usage`] || 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold text-[#0A2540]">{Number(r[`${utility}_amount`] || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportsTab() {
  const SECTIONS = [
    { v: 'subscribers',         l: 'Subscribers' },
    { v: 'revenue',             l: 'Revenue' },
    { v: 'agents',              l: 'Agents' },
    { v: 'impacts',             l: 'Impacts' },
    { v: 'utilities-royalties', l: 'Utilities & Royalties' },
  ];
  const [sub, setSub] = useState('subscribers');

  return (
    <div className="space-y-6" data-testid="reports-tab">
      <Tabs value={sub} onValueChange={setSub} className="space-y-6">
        <div className="inline-flex rounded-xl border border-[#EDE6D8] bg-white p-1">
          <TabsList
            className="bg-transparent p-0 h-auto flex flex-wrap gap-1"
            data-testid="reports-sub-tabs"
          >
            {SECTIONS.map((s) => (
              <TabsTrigger
                key={s.v}
                value={s.v}
                data-testid={`reports-tab-${s.v}`}
                className="px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-[0.18em] font-semibold
                           text-[#78716C] bg-transparent
                           data-[state=active]:bg-[#0A2540] data-[state=active]:text-white
                           hover:text-[#0A2540] transition-colors"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {s.l}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="subscribers" className="mt-0 focus-visible:outline-none">
          <SubscribersReports />
        </TabsContent>
        <TabsContent value="revenue" className="mt-0 focus-visible:outline-none">
          <RevenueReports />
        </TabsContent>
        <TabsContent value="agents" className="mt-0 focus-visible:outline-none">
          <AgentsReports />
        </TabsContent>
        {SECTIONS.filter((s) => !['subscribers', 'revenue', 'agents'].includes(s.v)).map((s) => (
          <TabsContent
            key={s.v}
            value={s.v}
            className="mt-0 focus-visible:outline-none"
          >
            <ComingSoon
              icon={FileText}
              title="Coming soon"
              blurb="This report section is being prepared and will be available shortly."
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

/* ─── Subscribers section ─────────────────────────────────────────────── */

const _yyyymm_iso = (d) => d.toISOString().slice(0, 10);

function SubscribersReports() {
  // shared "default = today" range
  const { entityId } = useEntityScope();
  const now = new Date();
  const today_iso = _yyyymm_iso(now);

  const [summaryRange, setSummaryRange] = useState({ start: today_iso, end: today_iso });
  const [listRange, setListRange]       = useState({ start: today_iso, end: today_iso });
  const [exportFilters, setExportFilters] = useState({
    performance: '', campus: '', category: '',
  });
  // Which folder is currently active. Null = collapsed grid on landing.
  const [open, setOpen] = useState(null);

  // Load campus & category dropdown options once on mount.
  const [campuses,   setCampuses]   = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, k] = await Promise.all([
          axios.get(`${API_URL}/api/campuses`,                 { headers: authHeaders() }),
          axios.get(`${API_URL}/api/subscriber-categories`,    { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        setCampuses(Array.isArray(c.data) ? c.data : []);
        setCategories(Array.isArray(k.data) ? k.data : []);
      } catch {
        // dropdowns just stay empty — user can still hit "All"
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const trigger = async (url, filename) => {
    try {
      const res = await axios.get(url, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e, 'Download failed'));
    }
  };

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="subscribers-reports"
    >
      <ReportFolderCard
        title="Registration Summary Report"
        blurb="New registrations grouped by category, campus and gender for the selected period."
        keyName="registration-summary"
        active={open}
        setActive={setOpen}
        testid="folder-registration-summary"
        downloadTestid="dl-registration-summary"
        panelTestid="panel-registration-summary"
        onDownload={() => trigger(
          `${API_URL}/api/reports/subscribers/registration-summary?start_date=${summaryRange.start}&end_date=${summaryRange.end}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `registration-summary_${summaryRange.start}_${summaryRange.end}.xlsx`,
        )}
      >
        <DateRangeInline value={summaryRange} onChange={setSummaryRange} />
      </ReportFolderCard>

      <ReportFolderCard
        title="Registration List Report"
        blurb="One row per subscriber registered in the selected period."
        keyName="registration-list"
        active={open}
        setActive={setOpen}
        testid="folder-registration-list"
        downloadTestid="dl-registration-list"
        panelTestid="panel-registration-list"
        onDownload={() => trigger(
          `${API_URL}/api/reports/subscribers/registration-list?start_date=${listRange.start}&end_date=${listRange.end}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `registrations_${listRange.start}_${listRange.end}.xlsx`,
        )}
      >
        <DateRangeInline value={listRange} onChange={setListRange} />
      </ReportFolderCard>

      <ReportFolderCard
        title="Subscribers List Report"
        blurb="Full subscriber book filtered by performance, campus or category."
        keyName="subscribers-export"
        active={open}
        setActive={setOpen}
        testid="folder-subscribers-export"
        downloadTestid="dl-subscribers-export"
        panelTestid="panel-subscribers-export"
        onDownload={() => {
          const qs = new URLSearchParams();
          if (exportFilters.performance) qs.set('performance', exportFilters.performance);
          if (exportFilters.campus)      qs.set('campus',      exportFilters.campus);
          if (exportFilters.category)    qs.set('category',    exportFilters.category);
          if (entityId && entityId !== 'all') qs.set('entity_id', entityId);
          const url = `${API_URL}/api/reports/subscribers/export${qs.toString() ? `?${qs}` : ''}`;
          trigger(url, `subscribers_${today_iso}.xlsx`);
        }}
      >
        <div className="grid grid-cols-1 gap-2">
          <SelectInput
            value={exportFilters.performance}
            onChange={(v) => setExportFilters((p) => ({ ...p, performance: v }))}
            testid="export-performance"
            options={[
              { v: '',          l: 'All performance' },
              { v: 'excellent', l: 'Excellent' },
              { v: 'good',      l: 'Good' },
              { v: 'normal',    l: 'Normal' },
              { v: 'dormant',   l: 'Dormant' },
            ]}
          />
          <SelectInput
            value={exportFilters.campus}
            onChange={(v) => setExportFilters((p) => ({ ...p, campus: v }))}
            testid="export-campus"
            options={[
              { v: '', l: 'All campuses' },
              ...campuses.map((c) => ({ v: c.campus_name, l: c.campus_name })),
            ]}
          />
          <SelectInput
            value={exportFilters.category}
            onChange={(v) => setExportFilters((p) => ({ ...p, category: v }))}
            testid="export-category"
            options={[
              { v: '', l: 'All categories' },
              ...categories.map((k) => ({ v: k.category, l: k.category })),
            ]}
          />
        </div>
      </ReportFolderCard>

      <ReportFolderCard
        title="Subscribers Summary Report"
        blurb="One-click snapshot of the full book: by category, campus, gender and performance."
        keyName="subscribers-summary"
        active={open}
        setActive={setOpen}
        testid="folder-subscribers-summary"
        downloadTestid="dl-subscribers-summary"
        panelTestid="panel-subscribers-summary"
        onDownload={() => trigger(
          `${API_URL}/api/reports/subscribers/summary${_scopeQS(entityId) ? `?${_scopeQS(entityId)}` : ''}`,
          `subscribers-summary_${today_iso}.xlsx`,
        )}
      >
        {null}
      </ReportFolderCard>
    </div>
  );
}

/* ─── Revenue section ─────────────────────────────────────────────────── */

function RevenueReports() {
  const { entityId } = useEntityScope();
  const now = new Date();
  const today_iso = _yyyymm_iso(now);
  const this_month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [campusesRange, setCampusesRange] = useState({ start: today_iso, end: today_iso });
  const [rvrMonth, setRvrMonth] = useState(this_month);
  const last_month = (() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [popMonths, setPopMonths] = useState({ a: last_month, b: this_month });
  const [open, setOpen] = useState(null);

  const trigger = async (url, filename) => {
    try {
      const res = await axios.get(url, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e, 'Download failed'));
    }
  };

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="revenue-reports"
    >
      <ReportFolderCard
        title="Campuses Revenue"
        blurb="Per-campus revenue (recharges, sub fees, renewals) for the selected date range, with overall total."
        keyName="campuses-revenue"
        active={open}
        setActive={setOpen}
        testid="folder-campuses-revenue"
        downloadTestid="dl-campuses-revenue"
        panelTestid="panel-campuses-revenue"
        onDownload={() => trigger(
          `${API_URL}/api/reports/revenue/campuses-revenue?start_date=${campusesRange.start}&end_date=${campusesRange.end}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `campuses-revenue_${campusesRange.start}_${campusesRange.end}.xlsx`,
        )}
      >
        <DateRangeInline value={campusesRange} onChange={setCampusesRange} />
      </ReportFolderCard>

      <ReportFolderCard
        title="Recharges vs Refills"
        blurb="Per-campus recharges, refills (all stores) and unspent wallet balance for the selected month."
        keyName="recharges-vs-refills"
        active={open}
        setActive={setOpen}
        testid="folder-recharges-vs-refills"
        downloadTestid="dl-recharges-vs-refills"
        panelTestid="panel-recharges-vs-refills"
        onDownload={() => trigger(
          `${API_URL}/api/reports/revenue/recharges-vs-refills?month=${rvrMonth}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `recharges-vs-refills_${rvrMonth}.xlsx`,
        )}
      >
        <MonthInline value={rvrMonth} onChange={setRvrMonth} testid="rvr-month" />
      </ReportFolderCard>

      <ReportFolderCard
        title="Period-over-Period Comparison"
        blurb="Revenue components and per-campus store sales + fees between two months (Period A vs Period B) with delta amount, delta % and verdict."
        keyName="period-comparison"
        active={open}
        setActive={setOpen}
        testid="folder-period-comparison"
        downloadTestid="dl-period-comparison"
        panelTestid="panel-period-comparison"
        onDownload={() => trigger(
          `${API_URL}/api/reports/revenue/period-comparison?month_a=${popMonths.a}&month_b=${popMonths.b}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `period-comparison_${popMonths.a}_vs_${popMonths.b}.xlsx`,
        )}
      >
        <div className="grid grid-cols-2 gap-2">
          <MonthInline
            value={popMonths.a}
            onChange={(v) => setPopMonths((p) => ({ ...p, a: v }))}
            testid="pop-month-a"
            label="Period A"
          />
          <MonthInline
            value={popMonths.b}
            onChange={(v) => setPopMonths((p) => ({ ...p, b: v }))}
            testid="pop-month-b"
            label="Period B"
          />
        </div>
      </ReportFolderCard>
    </div>
  );
}

/* ─── Agents section (Payment Orders + Float Logs) ────────────────────── */

function AgentsReports() {
  const { entityId } = useEntityScope();
  const now = new Date();
  const today_iso = _yyyymm_iso(now);

  const [poRange, setPoRange] = useState({ start: today_iso, end: today_iso });
  const [flRange, setFlRange] = useState({ start: today_iso, end: today_iso });
  const [open, setOpen] = useState(null);

  const trigger = async (url, filename) => {
    try {
      const res = await axios.get(url, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e, 'Download failed'));
    }
  };

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid="agents-reports"
    >
      <ReportFolderCard
        title="Payment Orders Report"
        blurb="Control-number payment orders raised across the network in the selected date range, with paid/pending status and totals."
        keyName="agents-payment-orders"
        active={open}
        setActive={setOpen}
        testid="folder-agents-payment-orders"
        downloadTestid="dl-agents-payment-orders"
        panelTestid="panel-agents-payment-orders"
        onDownload={() => trigger(
          `${API_URL}/api/reports/agents/payment-orders?start_date=${poRange.start}&end_date=${poRange.end}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `payment-orders_${poRange.start}_${poRange.end}.xlsx`,
        )}
      >
        <DateRangeInline value={poRange} onChange={setPoRange} />
      </ReportFolderCard>

      <ReportFolderCard
        title="Agent Float Logs Report"
        blurb="Per-agent float assignments and consumption (recharge, sub. reg, renewal, bottle) with remaining balance and revenue, over the selected date range."
        keyName="agents-float-logs"
        active={open}
        setActive={setOpen}
        testid="folder-agents-float-logs"
        downloadTestid="dl-agents-float-logs"
        panelTestid="panel-agents-float-logs"
        onDownload={() => trigger(
          `${API_URL}/api/reports/agents/float-logs?start_date=${flRange.start}&end_date=${flRange.end}${_scopeQS(entityId) ? `&${_scopeQS(entityId)}` : ''}`,
          `agent-float-logs_${flRange.start}_${flRange.end}.xlsx`,
        )}
      >
        <DateRangeInline value={flRange} onChange={setFlRange} />
      </ReportFolderCard>
    </div>
  );
}



/* ─── Self-contained folder card (expands inline with filters + download) ─ */

function ReportFolderCard({
  title, blurb, keyName, active, setActive, children,
  onDownload, testid, downloadTestid, panelTestid,
}) {
  const isActive = active === keyName;
  return (
    <div
      data-testid={testid}
      className={`flex flex-col gap-3 p-5 rounded-2xl border transition
        ${isActive
          ? 'border-[#0A2540] bg-[#FCFAF6] shadow-sm'
          : 'border-[#EDE6D8] bg-white hover:border-[#0A2540]/40 hover:bg-[#FCFAF6]/60'}`}
    >
      <button
        type="button"
        onClick={() => setActive(isActive ? null : keyName)}
        aria-expanded={isActive}
        className="flex items-start gap-3 text-left"
      >
        <FolderOpen
          size={42}
          weight="duotone"
          className={`shrink-0 transition-colors ${
            isActive ? 'text-[#C9A35B]' : 'text-[#0A2540] group-hover:text-[#C9A35B]'
          }`}
        />
        <div className="min-w-0 flex-1">
          <p
            className="text-sm font-semibold text-[#1C1917] leading-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {title}
          </p>
          <p className="text-[11px] text-[#78716C] mt-1 leading-snug">{blurb}</p>
        </div>
      </button>

      {isActive && (
        <div
          className="border-t border-[#EDE6D8] pt-3 flex flex-col gap-3"
          data-testid={panelTestid}
        >
          {children}
          <DownloadButton onClick={onDownload} testid={downloadTestid} />
        </div>
      )}
    </div>
  );
}

function MonthInline({ value, onChange, testid, label = 'Month' }) {
  return (
    <label className="space-y-1 block">
      <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold">{label}</span>
      <input
        type="month"
        className="w-full h-9 text-xs px-2 rounded-md border border-[#EDE6D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}


function DateRangeInline({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold">Start</span>
        <input
          type="date"
          className="w-full h-9 text-xs px-2 rounded-md border border-[#EDE6D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
          value={value.start}
          onChange={(e) => onChange({ ...value, start: e.target.value })}
        />
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold">End</span>
        <input
          type="date"
          className="w-full h-9 text-xs px-2 rounded-md border border-[#EDE6D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
          value={value.end}
          onChange={(e) => onChange({ ...value, end: e.target.value })}
        />
      </label>
    </div>
  );
}

function SelectInput({ value, onChange, options, testid }) {
  return (
    <select
      className="w-full h-9 text-xs px-2 rounded-md border border-[#EDE6D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  );
}

function DownloadButton({ onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-[#0A2540] text-white text-xs font-semibold uppercase tracking-wider transition hover:bg-[#0A2540]/90"
    >
      <DownloadSimple size={14} weight="bold" /> Download
    </button>
  );
}
